import React, { Fragment, useMemo, useState } from 'react';
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Transition,
} from '@headlessui/react';
import { ChevronUpDownIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useLocationContext } from '../../hooks/useLocationContext';
import {
  useGetClassesQuery,
  useBulkEnrollStudentsMutation,
  useGetClassTermsQuery,
} from '../../store/api/localApi';
import Modal from '../../components/Modal';
import ClassDayPicker from '../../components/classes/ClassDayPicker';
import EffectiveFromField, {
  effectiveFromErrorMessage,
} from '../../components/classes/EffectiveFromField';
import { coveringTerm } from '../../utils/terms';
import { dublinDateString } from '../../utils/date';

interface BulkClassModalProps {
  studentIds: string[];
  onClose: () => void;
  onSuccess: () => void;
}

interface ClassOption {
  id: string;
  name: string;
  isActive?: boolean;
}

/**
 * Bulk-enrol selected students into one class for the same set of days.
 * Backend rejects the whole batch if any day would breach capacity (total
 * batch demand counts).
 */
const BulkClassModal: React.FC<BulkClassModalProps> = ({ studentIds, onClose, onSuccess }) => {
  const { effectiveLocationId: activeLocationId } = useLocationContext();
  const [selectedClassId, setSelectedClassId] = useState('');
  const [days, setDays] = useState<number[]>([]);
  const [result, setResult] = useState<string | null>(null);
  // One line per child skipped because they are still in the class until a
  // later last day — the count alone would not say why.
  const [stillEnrolledNotes, setStillEnrolledNotes] = useState<string[]>([]);
  // One From date for the whole batch.
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [serverEarliest, setServerEarliest] = useState<string | undefined>();
  const [dateError, setDateError] = useState('');

  const { data: classesResponse } = useGetClassesQuery(activeLocationId || '');
  const classes = classesResponse || [];
  const selectedClass = classes.find((c) => c.id === selectedClassId);

  const [bulkEnroll, { isLoading }] = useBulkEnrollStudentsMutation();
  // Bulk enrolment books the term running today; its last day caps the From date.
  const { data: terms } = useGetClassTermsQuery(selectedClassId, { skip: !selectedClassId });
  const runningTerm = coveringTerm(terms ?? [], dublinDateString());

  const handleSubmit = async () => {
    if (!selectedClassId || days.length === 0 || !effectiveFrom) return;
    setDateError('');
    try {
      const res = await bulkEnroll({
        classId: selectedClassId,
        students: studentIds.map((studentId) => ({ studentId, daysOfWeek: days, effectiveFrom })),
      }).unwrap();
      // The date is one for the batch, so a refused date refuses every child
      // alike: keep the form open with the reason instead of a "failed" tally.
      const dateRefusal = (res.errors || []).find((e) =>
        ['EFFECTIVE_FROM_TOO_EARLY', 'EFFECTIVE_FROM_AFTER_TERM'].includes(e.error)
      );
      if (res.enrolled === 0 && dateRefusal) {
        const problem = effectiveFromErrorMessage({
          ...dateRefusal,
          termEndDate: runningTerm?.endDate,
        });
        setDateError(problem?.message ?? 'This date cannot be used. Pick another.');
        if (problem?.earliest) {
          setServerEarliest(problem.earliest);
          setEffectiveFrom(problem.earliest);
        }
        return;
      }
      // A student with no name recorded yet (staff "Add Student" before the
      // parent registers) is skipped, not failed silently — call that out by
      // name so it doesn't read as an unexplained partial failure.
      const missingNameCount = (res.errors || []).filter(
        (e) => e.error === 'STUDENT_MISSING_NAME'
      ).length;
      const alreadyCount = (res.errors || []).filter((e) => e.error === 'ALREADY_ENROLLED').length;
      const otherFailedCount = res.failed - missingNameCount - alreadyCount;
      const notes = [
        missingNameCount > 0
          ? `${missingNameCount} skipped (no name recorded yet — complete their registration first)`
          : null,
        alreadyCount > 0 ? `${alreadyCount} already in this class` : null,
        otherFailedCount > 0 ? `${otherFailedCount} failed` : null,
      ].filter(Boolean);
      const failedNote = notes.length > 0 ? `, ${notes.join(', ')}` : '';
      setStillEnrolledNotes(
        (res.errors || [])
          .filter((e) => e.error === 'ALREADY_ENROLLED' && e.until && e.message)
          .map((e) => e.message as string)
      );
      setResult(`Enrolled ${res.enrolled} student${res.enrolled !== 1 ? 's' : ''}${failedNote}`);
      setTimeout(onSuccess, 1500);
    } catch (err: any) {
      if (err?.data?.error === 'CAPACITY_EXCEEDED') {
        const breaches = err.data.breaches || [];
        setResult(
          `Error: capacity exceeded on day(s) ${breaches
            .map((b: any) => b.day)
            .join(', ')}. Adjust your selection.`
        );
      } else if (err?.data?.error === 'INVALID_ENROLLMENT_DAYS') {
        setResult('Error: selected days are outside the class schedule.');
      } else if (err?.data?.code === 'NO_RUNNING_TERM') {
        // Bulk enrolment deliberately stays today-only (no per-student term
        // choice to offer in a batch) — see resolveTermForEnrollmentToday in
        // manage-enrollments/index.ts. Between terms, use the single-student
        // Enroll flow instead, which supports booking into an upcoming term.
        setResult(
          'This class has no term running today. To add students ahead of the next term, enroll them one at a time from the class roster instead.'
        );
      } else if (err?.data?.code === 'STALE_ENROLLMENT' || err?.data?.error === 'STALE_ENROLLMENT') {
        setDateError(effectiveFromErrorMessage(err.data)?.message ?? '');
      } else {
        setResult(`Error: ${err?.data?.message || err?.data?.error || 'Operation failed'}`);
      }
    }
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={`Enroll ${studentIds.length} Student${studentIds.length !== 1 ? 's' : ''}`}
      maxWidth="md"
      testId="bulk-enroll-modal"
    >
      {result ? (
        <div
          data-testid="bulk-enroll-result"
          className={`p-3 rounded-md text-sm ${
            result.startsWith('Error') ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'
          }`}
        >
          {result}
          {stillEnrolledNotes.map((note) => (
            <p key={note} className="mt-1" data-testid="bulk-enroll-still-enrolled-note">
              {note}
            </p>
          ))}
        </div>
      ) : (
        <>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Class</label>
            <SingleClassPicker
              classes={classes}
              selectedClassId={selectedClassId}
              onPick={(id) => {
                setSelectedClassId(id);
                setDays([]);
                setEffectiveFrom('');
                setServerEarliest(undefined);
                setDateError('');
              }}
              disabled={isLoading}
            />
          </div>

          {selectedClass && (
            <div className="mb-4">
              <ClassDayPicker
                classId={selectedClass.id}
                className={selectedClass.name}
                value={days}
                onChange={setDays}
                disabled={isLoading}
              />
              <p className="mt-2 text-xs text-gray-500">
                Each of the {studentIds.length} selected student
                {studentIds.length === 1 ? '' : 's'} will be enrolled on the picked days. If the
                batch would exceed capacity on any day, the whole request is rejected.
              </p>
              <div className="mt-4">
                <EffectiveFromField
                  testId="bulk-from"
                  classId={selectedClass.id}
                  locationId={activeLocationId || ''}
                  value={effectiveFrom}
                  onChange={setEffectiveFrom}
                  notBefore={serverEarliest}
                  max={runningTerm?.endDate}
                  disabled={isLoading}
                />
              </div>
            </div>
          )}

          {dateError && (
            <p className="mb-3 text-sm text-red-600" data-testid="enrollment-action-error">
              {dateError}
            </p>
          )}

          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={!selectedClassId || days.length === 0 || !effectiveFrom || isLoading}
              data-testid="bulk-confirm-btn"
              className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white disabled:opacity-50 bg-primary-600 hover:bg-primary-700"
            >
              {isLoading ? 'Processing...' : 'Enroll'}
            </button>
          </div>
        </>
      )}
    </Modal>
  );
};

/**
 * Single-pick searchable class combobox. Matches the "Add a class…" picker
 * on AddStudentModal but in single-select mode — selecting an option sets
 * the value and closes the dropdown; a small × clears it. Native <select>
 * dropped because OS chrome looks out of place inside the modal styling.
 */
const SingleClassPicker: React.FC<{
  classes: ClassOption[];
  selectedClassId: string;
  onPick: (classId: string) => void;
  disabled?: boolean;
}> = ({ classes, selectedClassId, onPick, disabled }) => {
  const [query, setQuery] = useState('');

  const selected = useMemo(
    () => classes.find((c) => c.id === selectedClassId) || null,
    [classes, selectedClassId]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return classes;
    return classes.filter((cls) => cls.name.toLowerCase().includes(q));
  }, [classes, query]);

  return (
    <Combobox
      value={selected}
      onChange={(value: ClassOption | null) => {
        if (value) {
          onPick(value.id);
          setQuery('');
        }
      }}
      disabled={disabled}
      immediate
    >
      <div className="relative">
        <div className="relative w-full cursor-default rounded-md border border-gray-300 bg-white text-left shadow-sm focus-within:border-primary-500 focus-within:ring-1 focus-within:ring-primary-500">
          <ComboboxInput
            data-testid="bulk-class-picker-input"
            className="w-full bg-transparent border-0 py-2.5 pl-3 pr-16 text-sm text-gray-900 placeholder:text-gray-400 focus:ring-0 focus:outline-none"
            placeholder="Choose a class…"
            displayValue={(c: ClassOption | null) => c?.name ?? ''}
            onChange={(e) => setQuery(e.target.value)}
          />
          <div className="absolute inset-y-0 right-0 flex items-center pr-2 gap-1">
            {selected && !disabled && (
              <button
                type="button"
                onClick={() => {
                  onPick('');
                  setQuery('');
                }}
                aria-label="Clear class"
                data-testid="bulk-class-picker-clear"
                className="inline-flex items-center justify-center h-6 w-6 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100"
              >
                <XMarkIcon className="h-3.5 w-3.5" />
              </button>
            )}
            <ComboboxButton
              className="inline-flex items-center justify-center h-6 w-6 rounded text-gray-400 hover:text-gray-700"
              aria-label="Open class picker"
            >
              <ChevronUpDownIcon className="h-4 w-4" />
            </ComboboxButton>
          </div>
        </div>
        <Transition
          as={Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
          afterLeave={() => setQuery('')}
        >
          <ComboboxOptions
            data-testid="bulk-class-picker-options"
            className="absolute z-10 mt-1 max-h-56 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black/5 focus:outline-none"
          >
            {filtered.length === 0 ? (
              <div className="cursor-default select-none px-3 py-2 text-sm text-gray-500">
                {query ? `No class matches "${query}"` : 'No classes available'}
              </div>
            ) : (
              filtered.map((cls) => (
                <ComboboxOption
                  key={cls.id}
                  value={cls}
                  data-testid={`bulk-class-picker-option-${cls.id}`}
                  className={({ active }) =>
                    `relative cursor-pointer select-none px-3 py-2 ${
                      active ? 'bg-primary-50 text-primary-900' : 'text-gray-900'
                    }`
                  }
                >
                  {cls.name}
                </ComboboxOption>
              ))
            )}
          </ComboboxOptions>
        </Transition>
      </div>
    </Combobox>
  );
};

export default BulkClassModal;
